import { Schema } from 'effect'
import { match } from '@st/pattern'
import type { AppRoute, ClientPacketsFlowId, SignUpFlowId } from '~/features/routes'

export type { SignUpFlowId } from '~/features/routes'

export type OrganizationSignUpInput = {
  userName?: string
  userEmail: string
  phoneNumber?: string
  password: string
  passwordConfirm: string
  organizationName: string
  taxApp?: string
  numberOfClients?: string
  practiceManagementApp?: string
  tosPrivacyAgreement: boolean
}

export type OrganizationSignUpFieldErrors = Partial<Record<keyof OrganizationSignUpInput, string>>

function requiredString(message: string) {
  return Schema.String.annotate({ message })
    .check(Schema.isNonEmpty({ message }))
    .pipe(Schema.annotateKey({ messageMissingKey: message }))
}

// A local test account accepts no legal agreement; every other sign-up must agree.
const commonFields = (termsRequired: boolean) => ({
  userName: Schema.optional(Schema.String),
  userEmail: Schema.String,
  organizationName: Schema.String.check(
    Schema.makeFilter((name) => {
      if (name.length == 0) return 'Enter a company name. You can always change it later.'
      if (name.length < 3) return 'Your company name must be at least 3 characters long.'
    })
  ),
  tosPrivacyAgreement: termsRequired
    ? Schema.Literal(true).annotate({
        message: 'Please agree to the Terms of Service and Privacy Policy'
      })
    : Schema.Boolean
})

const accountFields = {
  userEmail: requiredString('An email is required'),
  password: requiredString('A password is required'),
  passwordConfirm: requiredString('A password confirmation is required')
}

const organizationProfileFields = {
  phoneNumber: Schema.optional(Schema.String),
  taxApp: requiredString('Please select the tax software you use'),
  numberOfClients: requiredString('Please select the number of clients you have'),
  practiceManagementApp: requiredString('Please select the practice management platform you use')
}

const passwordsMatch = Schema.makeFilter(
  (input: { readonly password: string; readonly passwordConfirm: string }) => {
    if (input.passwordConfirm.length > 0 && input.password != input.passwordConfirm) {
      return {
        path: ['passwordConfirm'],
        issue: 'The password confirmation does not match the original password'
      }
    }
  }
)

const commonForms = (termsRequired: boolean) => {
  const fields = commonFields(termsRequired)
  return {
    loggedInSchema: Schema.Struct(fields),
    loggedOutSchema: Schema.Struct({ ...fields, ...accountFields }).check(passwordsMatch)
  }
}
const profileForms = (termsRequired: boolean) => {
  const fields = { ...commonFields(termsRequired), ...organizationProfileFields }
  return {
    loggedInSchema: Schema.Struct(fields),
    loggedOutSchema: Schema.Struct({ ...fields, ...accountFields }).check(passwordsMatch)
  }
}
const toFolderList = (organizationSlug: string): AppRoute => ({
  name: 'folder_list',
  organizationSlug
})
const toClientPacketsUpload =
  (flow: ClientPacketsFlowId) =>
  (organizationSlug: string): AppRoute => ({
    name: 'client_packets_upload_backup',
    organizationSlug,
    flow
  })

const defaultFlow = {
  forms: profileForms,
  headline: `You're almost in!`,
  submitLabel: 'Create free account',
  completionRoute: toFolderList
}

const clientPacketsFlow = {
  forms: profileForms,
  headline: 'Create your free account',
  submitLabel: 'Create free account',
  completionRoute: toClientPacketsUpload('personalized-checklists')
}

const personalizedPdfFlow = {
  forms: commonForms,
  headline: 'Create a free personalized tax organizer PDF for every client',
  submitLabel: 'Create my free organizer PDFs',
  completionRoute: toClientPacketsUpload('personalized-pdf')
}

export type AccountCreationInput = Schema.Struct.Type<typeof accountFields> & {
  readonly userName?: string | undefined
}

const organizationCreationSchema = Schema.Struct({
  organizationName: Schema.String,
  userEmail: Schema.String,
  phoneNumber: Schema.optional(Schema.String),
  taxApp: Schema.optional(Schema.String),
  numberOfClients: Schema.optional(Schema.String),
  practiceManagementApp: Schema.optional(Schema.String)
})

export type OrganizationCreationInput = typeof organizationCreationSchema.Type

export const toOrganizationCreationInput: (
  input: OrganizationCreationInput
) => OrganizationCreationInput = Schema.decodeUnknownSync(organizationCreationSchema)

export function organizationSignUpFlow(flow: SignUpFlowId | undefined) {
  return match(flow)
    .with('client-packets', () => clientPacketsFlow)
    .with('personalized-checklists', () => clientPacketsFlow)
    .with('personalized-pdf', () => personalizedPdfFlow)
    .with(undefined, () => defaultFlow)
    .exhaustive()
}
